import { checkTemplateSource, extractPdfLines, templateActivationBlocked, type ContractRole, type CustomVariableDef, type Language } from "@talentkontor/domain";
import { readZipText, unescapeXml, zipEntries, type ZipMeldungen } from "../../../lib/unzip";
import { t } from "../../../lib/sprache";
import { findingText } from "./pruefen";

/**
 * Eigene Verträge aus Word (.docx) oder PDF übernehmen (Entscheidung des Inhabers 07.10.2026): Text lesen, als Vertragstext mit
 * erlaubter Auszeichnung aufbauen, Parteien, Beträge und Daten erkennen und als Platzhalter vorschlagen. Die Agentur bestätigt
 * oder ändert jeden Vorschlag; danach entsteht ein Entwurf, den sie im Vorlagen-Editor prüft und aktiviert.
 * Ohne Bibliothek: .docx ist ein ZIP mit XML (lib/unzip), PDF liest der Textleser der Fachlogik (wie der Rechnungsimport).
 */

export interface ImportRun { text: string; bold: boolean }
export interface ImportBlock { type: "h1" | "h2" | "p" | "li"; runs: ImportRun[] }

const blockText = (b: ImportBlock) => b.runs.map((r) => r.text).join("");

// ── Word ────────────────────────────────────────────────────────────────────────────

const WORD_MELDUNGEN = (): ZipMeldungen => ({
  invalid: t("Die Datei ist keine gültige Word-Datei (.docx).", "The file is not a valid Word file (.docx)."),
  damaged: t("Die Word-Datei ist beschädigt.", "The Word file is damaged."),
  unknownMethod: t("Diese Word-Datei ist mit einem unbekannten Verfahren gepackt. Bitte als .docx neu speichern oder als PDF hochladen.", "This Word file is packed with an unknown method. Please save it again as .docx or upload a PDF."),
});

/** Absatzformat → Überschrift: „Title“/„Titel“ und Überschrift 1 → h1, Überschrift 2 bis 4 → h2 (Word-IDs auch auf Deutsch: „berschrift1“) */
function headingOf(style: string | null): "h1" | "h2" | null {
  if (!style) return null;
  if (/^(title|titel)$/i.test(style)) return "h1";
  const m = /^(?:heading|berschrift|überschrift|ueberschrift)\s*(\d)$/i.exec(style);
  if (!m) return null;
  return m[1] === "1" ? "h1" : "h2";
}

/** Absätze eines word/document.xml als Blöcke (fett bleibt erhalten, Tabellenzellen werden zu Absätzen) */
export function blocksFromDocumentXml(xml: string): ImportBlock[] {
  const out: ImportBlock[] = [];
  for (const pm of xml.matchAll(/<w:p(?:\s[^>]*)?>([\s\S]*?)<\/w:p>|<w:p(?:\s[^>]*)?\/>/g)) {
    const p = pm[1] ?? "";
    const style = /<w:pStyle\s+w:val="([^"]+)"/.exec(p)?.[1] ?? null;
    const list = /<w:numPr>/.test(p);
    const runs: ImportRun[] = [];
    for (const rm of p.matchAll(/<w:r(?:\s[^>]*)?>([\s\S]*?)<\/w:r>/g)) {
      const r = rm[1]!;
      if (/<w:instrText/.test(r)) continue;
      const bold = /<w:b(?:\s+w:val="(?:1|true|on)")?\s*\/>/.test(r);
      let text = "";
      for (const tm of r.matchAll(/<w:t(?:\s[^>]*)?>([\s\S]*?)<\/w:t>|<w:tab\/>|<w:br(?:\s[^>]*)?\/>|<w:noBreakHyphen\/>/g)) {
        if (tm[1] !== undefined) text += unescapeXml(tm[1]);
        else if (tm[0].startsWith("<w:tab")) text += " ";
        else if (tm[0].startsWith("<w:br")) text += "\n";
        else text += "-";
      }
      if (!text) continue;
      const last = runs[runs.length - 1];
      if (last && last.bold === bold) last.text += text; else runs.push({ text, bold });
    }
    const plain = runs.map((x) => x.text).join("").trim();
    if (!plain) continue;
    out.push({ type: headingOf(style) ?? (list ? "li" : "p"), runs });
  }
  return out;
}

export async function readDocx(bytes: Uint8Array): Promise<ImportBlock[]> {
  const m = WORD_MELDUNGEN();
  const doc = zipEntries(bytes, m).find((f) => f.name === "word/document.xml");
  if (!doc) throw new Error(m.invalid);
  return blocksFromDocumentXml(await readZipText(bytes, doc, m));
}

// ── PDF ─────────────────────────────────────────────────────────────────────────────

const LIST_MARK = /^(?:\(?\d{1,2}\)|\d{1,2}\.(?!\d)|[a-z]\))\s+/;
// „§ 3 Vergütung“, „Artikel 2“; „3. Vergütung“ nur als kurze Zeile (bis vier Wörter), sonst ist es ein nummerierter Punkt
const PARA_HEAD = /^(?:§\s*\d+[a-z]?\b|Art(?:ikel)?\.?\s*\d+\b)/;
const NUM_HEAD = /^\d{1,2}\.\s+[A-ZÄÖÜ]\S*(?:\s+\S+){0,3}$/;
/** Eine Zeile ab dieser Länge ist meist umbrochen (Fließtext); kürzere Zeilen (Anschrift, „zwischen“) stehen für sich */
const WRAPPED = 45;

/** Zeilen einer PDF-Datei als Blöcke: Titel, „§ n“-Überschriften, nummerierte Punkte, Absätze aus fortlaufenden Zeilen */
export function blocksFromPdfLines(lines: readonly string[]): ImportBlock[] {
  const out: ImportBlock[] = [];
  const push = (type: ImportBlock["type"], text: string) => out.push({ type, runs: [{ text, bold: false }] });
  for (const raw of lines) {
    const line = raw.replace(/\s+/g, " ").trim();
    if (!line || /^(Seite|Page)\s+\d+(\s+(von|of)\s+\d+)?$/i.test(line)) continue;
    const prev = out[out.length - 1];
    if (!out.length && line.length <= 80) { push("h1", line); continue; }
    if ((PARA_HEAD.test(line) || NUM_HEAD.test(line)) && line.length <= 90 && !/[.;,]$/.test(line)) { push("h2", line); continue; }
    if (LIST_MARK.test(line)) { push("li", line.replace(LIST_MARK, "")); continue; }
    // Fortsetzung: vorige Zeile lang (umbrochen) und ohne Satzende → anhängen; kurze Zeilen bleiben eigene Absätze
    const prevLine = prev ? prev.runs[prev.runs.length - 1]!.text.split(" ").length : 0;
    // (Punkte einer Aufzählung laufen ohne Satzende immer weiter)
    if (prev && (prev.type === "li" || (prev.type === "p" && lastLineLength(prev) >= WRAPPED)) && prevLine > 0 && !/[.:;!?“"]$/.test(blockText(prev))) {
      prev.runs[prev.runs.length - 1]!.text += ` ${line}`;
      (prev as ImportBlock & { lastLine?: number }).lastLine = line.length;
      continue;
    }
    push("p", line);
  }
  return out;
}

/** Länge der zuletzt angehängten Zeile eines Blocks */
function lastLineLength(b: ImportBlock): number {
  return (b as ImportBlock & { lastLine?: number }).lastLine ?? blockText(b).length;
}

export async function readPdf(bytes: Uint8Array): Promise<ImportBlock[]> {
  let lines: string[];
  try {
    lines = await extractPdfLines(bytes, { maxPages: 40 });
  } catch (e) {
    throw new Error(/verschlüsselt/.test((e as Error).message)
      ? t("Die PDF-Datei ist verschlüsselt. Bitte ohne Kennwortschutz speichern.", "The PDF file is encrypted. Please save it without password protection.")
      : t("Die PDF-Datei lässt sich nicht lesen.", "The PDF file cannot be read."));
  }
  if (!lines.length) throw new Error(t("In der PDF-Datei steht kein lesbarer Text (eingescannt?). Bitte die Word-Datei nehmen.", "The PDF file contains no readable text (scanned?). Please use the Word file."));
  return blocksFromPdfLines(lines);
}

// ── Vorschläge ──────────────────────────────────────────────────────────────────────

export type SuggestionKind = "party_name" | "party_address" | "amount" | "date";
export interface ImportOption { value: string; label: string }
export interface Suggestion {
  id: string;
  kind: SuggestionKind;
  /** gefundener Text (Adresse: Zeilen mit „\n“) */
  text: string;
  occurrences: number;
  role?: ContractRole;
  options: ImportOption[];
  /** gewählter Platzhalter; "" = nicht ersetzen; "var:money" / "var:date" = eigene Angabe */
  choice: string;
}

const ROLE_WORDS: [ContractRole, RegExp][] = [
  ["talent", /creator|influencer|talent|künstler|kuenstler|content[- ]?ersteller/i],
  ["agency", /agentur|management|agency|vermittler/i],
  ["brand", /marke|auftraggeber|kunde|unternehmen|brand|client|customer|company|sponsor/i],
];
export function roleFromLabel(label: string): ContractRole | null {
  return ROLE_WORDS.find(([, re]) => re.test(label))?.[0] ?? null;
}
const LABEL = /\((?:nachfolgend|im Folgenden)[^„"“)]*[„"“]([^“"”]+)[“"”]\)|\(hereinafter(?: referred to as)?(?: the)? ?["“]([^"”]+)["”]\)/i;
const SEPARATOR = /^(zwischen|und|sowie|between|and|as well as|vermittelt und betreut durch|arranged and managed by)$/i;
const REPRESENTED = /^(vertreten durch|gesetzlich vertreten|represented by|vertretungsberechtigt)/i;

const NAME_OPTIONS = (): ImportOption[] => [
  { value: "{{brand.legal_name}}", label: t("Marke · Firmenname", "Brand · legal name") },
  { value: "{{talent.full_name}}", label: t("Creator · Name", "Creator · name") },
  { value: "{{agency.legal_name}}", label: t("Agentur bzw. dein Konto · Firmenname", "Agency or your account · legal name") },
  { value: "", label: t("Nicht ersetzen", "Do not replace") },
];
const ADDRESS_OPTIONS = (): ImportOption[] => [
  { value: "{{brand.address_block}}", label: t("Marke · Anschrift", "Brand · address") },
  { value: "{{talent.address_block}}", label: t("Creator · Anschrift", "Creator · address") },
  { value: "{{agency.address_block}}", label: t("Agentur bzw. dein Konto · Anschrift", "Agency or your account · address") },
  { value: "", label: t("Nicht ersetzen", "Do not replace") },
];
const AMOUNT_OPTIONS = (): ImportOption[] => [
  { value: "{{deal.total|money}}", label: t("Gesamtbetrag der Kooperation", "Total amount of the collaboration") },
  { value: "{{deal.budget|money}}", label: t("Budget der Kooperation", "Budget of the collaboration") },
  { value: "var:money", label: t("Eigene Angabe (beim Vertrag eintragen)", "Own value (entered per contract)") },
  { value: "", label: t("Nicht ersetzen", "Do not replace") },
];
const DATE_OPTIONS = (): ImportOption[] => [
  { value: "{{contract.date|date}}", label: t("Vertragsdatum", "Contract date") },
  { value: "{{deal.content_deadline|date}}", label: t("Frist für Inhalte", "Content deadline") },
  { value: "{{contract.starts_on|date}}", label: t("Vertragsbeginn", "Contract start") },
  { value: "{{contract.ends_on|date}}", label: t("Vertragsende", "Contract end") },
  { value: "var:date", label: t("Eigene Angabe (beim Vertrag eintragen)", "Own value (entered per contract)") },
  { value: "", label: t("Nicht ersetzen", "Do not replace") },
];
const ROLE_NAME: Record<ContractRole, string> = { brand: "{{brand.legal_name}}", talent: "{{talent.full_name}}", agency: "{{agency.legal_name}}" };
const ROLE_ADDRESS: Record<ContractRole, string> = { brand: "{{brand.address_block}}", talent: "{{talent.address_block}}", agency: "{{agency.address_block}}" };

const AMOUNT = /(?:(?:€|EUR)\s?\d{1,3}(?:[.  ]\d{3})*(?:,\d{2})?|\d{1,3}(?:[.  ]\d{3})*(?:,\d{2})?\s?(?:€|EUR\b|Euro\b))/g;
const DATE = /\b(?:\d{1,2}\.\s?\d{1,2}\.\s?\d{4}|\d{4}-\d{2}-\d{2})\b/g;

const countIn = (blocks: readonly ImportBlock[], s: string) => blocks.reduce((n, b) => n + blockText(b).split(s).length - 1, 0);

/** Parteien aus „… (nachfolgend „Marke“)“, Beträge und Daten als Vorschläge, in der Reihenfolge des Texts */
export function findSuggestions(blocks: readonly ImportBlock[]): Suggestion[] {
  const out: Suggestion[] = [];
  const seen = new Set<string>();
  let n = 0;
  blocks.forEach((b, i) => {
    const m = LABEL.exec(blockText(b));
    if (!m) return;
    const role = roleFromLabel(m[1] ?? m[2] ?? "");
    // Zeilen der Partei: rückwärts bis „zwischen“/„und“ oder einer Überschrift (höchstens fünf Absätze)
    const lines: string[] = [];
    for (let k = i; k >= 0 && k > i - 6; k--) {
      const text = blockText(blocks[k]!).trim();
      // vorige Partei endet mit ihrer eigenen „(nachfolgend …)“-Zeile
      if (k < i && (SEPARATOR.test(text) || LABEL.test(text) || blocks[k]!.type === "h1" || blocks[k]!.type === "h2")) break;
      lines.unshift(...text.replace(LABEL, "").split(/\n|,\s(?=\d{4,5}\s|[A-ZÄÖÜ][a-zäöüß]+(?:straße|weg|platz|gasse|allee))/).map((x) => x.trim()).filter(Boolean));
    }
    const kept = lines.filter((l) => !REPRESENTED.test(l) && !SEPARATOR.test(l));
    const name = kept[0];
    if (!name || seen.has(name)) return;
    seen.add(name);
    out.push({ id: `s${++n}`, kind: "party_name", text: name, occurrences: countIn(blocks, name), ...(role ? { role } : {}), options: NAME_OPTIONS(), choice: role ? ROLE_NAME[role] : "" });
    const address = kept.slice(1).filter((l) => /\d/.test(l) || kept.length <= 3);
    if (address.length) {
      out.push({ id: `s${++n}`, kind: "party_address", text: address.join("\n"), occurrences: 1, ...(role ? { role } : {}), options: ADDRESS_OPTIONS(), choice: role ? ROLE_ADDRESS[role] : "" });
    }
  });
  const amounts = new Map<string, number>();
  const dates = new Map<string, number>();
  blocks.forEach((b, i) => {
    for (const a of blockText(b).match(AMOUNT) ?? []) if (!amounts.has(a.trim())) amounts.set(a.trim(), i);
    for (const d of blockText(b).match(DATE) ?? []) if (!dates.has(d)) dates.set(d, i);
  });
  const nurEinBetrag = amounts.size === 1;
  for (const [a] of amounts) out.push({ id: `s${++n}`, kind: "amount", text: a, occurrences: countIn(blocks, a), options: AMOUNT_OPTIONS(), choice: nurEinBetrag ? "{{deal.total|money}}" : "" });
  let datumGesetzt = false;
  for (const [d, i] of dates) {
    const kopf = !datumGesetzt && i < 8;
    if (kopf) datumGesetzt = true;
    out.push({ id: `s${++n}`, kind: "date", text: d, occurrences: countIn(blocks, d), options: DATE_OPTIONS(), choice: kopf ? "{{contract.date|date}}" : "" });
  }
  return out;
}

// ── Vertragstext ────────────────────────────────────────────────────────────────────

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
// Steuerzeichen und unsichtbare Formatzeichen (außer Zeilenumbruch) entfernen, sonst lehnt die Prüfung den Text ab
// eslint-disable-next-line no-control-regex
const clean = (s: string) => s.replace(/[\u0000-\u0009\u000b-\u001f\u007f​-‍⁠﻿]/g, "");

export interface ImportResult { bodyHtml: string; variables: CustomVariableDef[]; requiredSigners: ContractRole[] }

/** Vorschläge anwenden und den Vertragstext bauen; Unterschriftsfelder der gewählten Parteien am Ende */
export function buildTemplate(blocks: readonly ImportBlock[], suggestions: readonly Suggestion[], signers: readonly ContractRole[], lang: Language): ImportResult {
  const variables: CustomVariableDef[] = [];
  const work: ImportBlock[] = blocks.map((b) => ({ type: b.type, runs: b.runs.map((r) => ({ ...r, text: clean(r.text) })) }));
  const token = (s: Suggestion): string | null => {
    if (!s.choice) return null;
    if (s.choice === "var:money" || s.choice === "var:date") {
      const money = s.choice === "var:money";
      const key = `${money ? "betrag" : "datum"}_${variables.filter((v) => v.type === (money ? "money" : "date")).length + 1}`;
      variables.push({ key, type: money ? "money" : "date", required: true,
        label: { de: `${money ? "Betrag" : "Datum"} (im Original: ${s.text})`, en: `${money ? "Amount" : "Date"} (in the original: ${s.text})` } });
      return `{{var.${key}|${money ? "money" : "date"}}}`;
    }
    return s.choice;
  };
  // Anschrift: erste Zeile durch den Platzhalter ersetzen, die übrigen Zeilen entfernen
  for (const s of suggestions.filter((x) => x.kind === "party_address")) {
    const tok = token(s);
    if (!tok) continue;
    const lines = s.text.split("\n");
    let first = true;
    for (const b of work) for (const r of b.runs) for (const line of lines) {
      if (!r.text.includes(line)) continue;
      r.text = r.text.replace(line, first ? tok : "");
      first = false;
    }
    // Reste der entfernten Zeilen („, , (nachfolgend …)“) aufräumen
    for (const b of work) for (const r of b.runs) r.text = r.text.replace(/,\s*(?=,)/g, "").replace(/,\s*(?=\((?:nachfolgend|im Folgenden|hereinafter))/gi, " ");
  }
  // Namen, Beträge, Daten: alle Vorkommen; über Formatwechsel hinweg notfalls im ganzen Absatz (ohne Fettschrift)
  for (const s of suggestions.filter((x) => x.kind !== "party_address")) {
    const tok = token(s);
    if (!tok) continue;
    for (const b of work) {
      let hit = false;
      for (const r of b.runs) if (r.text.includes(s.text)) { r.text = r.text.split(s.text).join(tok); hit = true; }
      if (!hit && blockText(b).includes(s.text)) b.runs = [{ text: blockText(b).split(s.text).join(tok), bold: false }];
    }
  }
  const html: string[] = [];
  let inList = false;
  for (const b of work) {
    const inner = b.runs.map((r) => {
      const text = esc(r.text).split("\n").map((x) => x.replace(/ {2,}/g, " ")).filter((x, i, a) => x.trim() || (i > 0 && i < a.length - 1)).join("<br>");
      return r.bold && text.trim() ? `<strong>${text}</strong>` : text;
    }).join("").replace(/(<br>\s*)+$/, "").replace(/^\s*(<br>\s*)+/, "").trim();
    if (!inner) continue;
    if (b.type === "li") { if (!inList) { html.push("<ol>"); inList = true; } html.push(`  <li>${inner}</li>`); continue; }
    if (inList) { html.push("</ol>"); inList = false; }
    html.push(b.type === "p" ? `<p>${inner}</p>` : `<${b.type}>${inner}</${b.type}>`);
  }
  if (inList) html.push("</ol>");
  const order: ContractRole[] = ["brand", "talent", "agency"];
  const sigs = order.filter((r) => signers.includes(r));
  if (sigs.length && !html.some((l) => l.includes("{{signature:"))) {
    html.push("", `<h2>${lang === "en" ? "Signatures" : "Unterschriften"}</h2>`, ...sigs.map((r) => `{{signature:${r}}}`));
  }
  return { bodyHtml: html.join("\n"), variables, requiredSigners: sigs };
}

/** Sperrende Befunde der Vertragsprüfung (wie beim Aktivieren); leer = als Entwurf anlegbar */
export function importProblems(r: ImportResult): string[] {
  const f = checkTemplateSource({ bodyHtml: r.bodyHtml, annexHtml: null, variables: r.variables, requiredSigners: r.requiredSigners, annexHiddenFrom: [] });
  return templateActivationBlocked(f) ? f.filter((x) => x.severity === "error").map((x) => findingText(x, r.variables)) : [];
}
